import { isBrazilCheckoutLocale } from "./plans.js";

/**
 * International phone helpers.
 *
 * We store phone numbers as E.164 strings with leading `+`
 * (e.g. `+5511999999999`). Both OpenPix and Stripe accept this format and
 * strip non-digits internally — see server/providers/openpix.ts where the
 * provider does `phone.replace(/\D/g, "")` before sending.
 *
 * The country list below is intentionally curated to the markets Zenpost
 * actually sells into (Brasil first, then LatAm + Portugal + Spain + US).
 * If you need to add more countries, drop them into ALL_COUNTRIES and they
 * become available without any other code changes.
 */

export interface Country {
  iso: string;            // ISO-3166-1 alpha-2, e.g. "BR"
  dial: string;           // E.164 country code with leading +, e.g. "+55"
  label: string;          // Human-readable name in the country's own language
  example?: string;       // Sample local-format number for placeholders
  /**
   * Validates the local part (without the dial code). If undefined, falls
   * back to a generic E.164 total-length check (8–15 digits).
   */
  validate?: (localDigits: string) => boolean;
}

/** Default country shown when none is detected from URL or stored value. */
export const DEFAULT_COUNTRY_ISO = "BR";

/** Whether this market uses CPF (Brazilian tax ID) at checkout. */
export function isBrazilCountryIso(iso: string | null | undefined): boolean {
  return (iso ?? DEFAULT_COUNTRY_ISO).toUpperCase() === "BR";
}

/**
 * CPF is required for Brazilian customers. Derived from the E.164 phone so it
 * stays in sync with the country picker in PhoneInput.
 */
export function requiresBrazilianCpf(phoneE164: string, defaultIso: string = DEFAULT_COUNTRY_ISO): boolean {
  return isBrazilCountryIso(countryIsoFromE164(phoneE164, defaultIso));
}

/**
 * CPF is mandatory for pt-BR checkout with a Brazilian phone — both PIX and card.
 * International locales (en-US / es-ES) never require CPF.
 */
export function needsCpfAtCheckout(
  locale: string | null | undefined,
  phoneE164: string,
  defaultIso: string = DEFAULT_COUNTRY_ISO,
): boolean {
  return isBrazilCheckoutLocale(locale) && requiresBrazilianCpf(phoneE164, defaultIso);
}

/** ISO-2 country inferred from an E.164 or partial phone string. */
export function countryIsoFromE164(
  phoneE164: string,
  defaultIso: string = DEFAULT_COUNTRY_ISO,
): string {
  return parsePhone(phoneE164, defaultIso).country.iso;
}

/** OpenPix / PIX is Brazil-only. */
export function isPixAvailableForPhone(phoneE164: string, defaultIso: string = DEFAULT_COUNTRY_ISO): boolean {
  return requiresBrazilianCpf(phoneE164, defaultIso);
}

// Ordered: priority markets first, then the rest alphabetically by label.
// Keep BR at the top — it's our default and 95%+ of traffic.
export const ALL_COUNTRIES: Country[] = [
  { iso: "BR", dial: "+55", label: "Brasil", example: "(11) 99999-9999",
    validate: (d) => d.length >= 10 && d.length <= 11 },
  { iso: "PT", dial: "+351", label: "Portugal", example: "912 345 678",
    validate: (d) => d.length === 9 },
  { iso: "US", dial: "+1", label: "United States", example: "(555) 555-5555",
    validate: (d) => d.length === 10 },
  { iso: "AR", dial: "+54", label: "Argentina", example: "11 1234-5678",
    validate: (d) => d.length >= 10 && d.length <= 11 },
  { iso: "MX", dial: "+52", label: "México", example: "55 1234 5678",
    validate: (d) => d.length === 10 },
  { iso: "CO", dial: "+57", label: "Colombia", example: "300 1234567",
    validate: (d) => d.length === 10 },
  { iso: "CL", dial: "+56", label: "Chile", example: "9 1234 5678",
    validate: (d) => d.length === 9 },
  { iso: "PE", dial: "+51", label: "Perú", example: "912 345 678",
    validate: (d) => d.length === 9 },
  { iso: "UY", dial: "+598", label: "Uruguay", example: "9 123 4567",
    validate: (d) => d.length === 8 },
  { iso: "PY", dial: "+595", label: "Paraguay", example: "961 123456",
    validate: (d) => d.length >= 9 && d.length <= 10 },
  { iso: "EC", dial: "+593", label: "Ecuador", example: "99 123 4567",
    validate: (d) => d.length === 9 },
  { iso: "BO", dial: "+591", label: "Bolivia", example: "71234567",
    validate: (d) => d.length === 8 },
  { iso: "VE", dial: "+58", label: "Venezuela", example: "412-1234567",
    validate: (d) => d.length === 10 },
  { iso: "ES", dial: "+34", label: "España", example: "612 34 56 78",
    validate: (d) => d.length === 9 },
  { iso: "CA", dial: "+1", label: "Canada", example: "(555) 555-5555",
    validate: (d) => d.length === 10 },
  { iso: "GB", dial: "+44", label: "United Kingdom", example: "7400 123456",
    validate: (d) => d.length >= 10 && d.length <= 11 },
  { iso: "FR", dial: "+33", label: "France", example: "6 12 34 56 78",
    validate: (d) => d.length === 9 },
  { iso: "DE", dial: "+49", label: "Deutschland", example: "151 12345678",
    validate: (d) => d.length >= 10 && d.length <= 11 },
  { iso: "IT", dial: "+39", label: "Italia", example: "312 345 6789",
    validate: (d) => d.length >= 9 && d.length <= 10 },
];

const BY_ISO = Object.fromEntries(ALL_COUNTRIES.map((c) => [c.iso, c]));

/** Find a country by ISO-2 code. Returns BR by default. */
export function getCountryByIso(iso: string | undefined | null): Country {
  if (iso && BY_ISO[iso.toUpperCase()]) return BY_ISO[iso.toUpperCase()]!;
  return BY_ISO[DEFAULT_COUNTRY_ISO]!;
}

/**
 * Turn an ISO-2 country code into the Unicode regional-indicator flag emoji.
 * "BR" -> "🇧🇷". Works in any browser that supports Unicode 6+ (all modern).
 */
export function isoToFlag(iso: string): string {
  return iso
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

/** Strip all non-digits. */
export function digitsOnly(input: string): string {
  return (input ?? "").replace(/\D/g, "");
}

/**
 * Returns the E.164 string (with leading "+") given a country and a local
 * digit string. Examples:
 *   toE164(BR, "11999999999")  -> "+5511999999999"
 *   toE164(US, "5555555555")   -> "+15555555555"
 */
export function toE164(country: Country, localDigits: string): string {
  return `${country.dial}${digitsOnly(localDigits)}`;
}

/**
 * Best-effort parser. Accepts:
 *   - E.164 strings ("+5511999999999")  ->  detects BR, local=11999999999
 *   - Bare digit strings prefixed by the country code ("5511999999999")
 *   - Plain local numbers ("11999999999")  ->  assumes the default country
 *
 * Returns `{ country, local }` so callers can seed both the country selector
 * and the local input from a single string (URL param, DB value, etc.).
 */
export function parsePhone(
  value: string | null | undefined,
  defaultIso: string = DEFAULT_COUNTRY_ISO,
): { country: Country; local: string } {
  const fallback = { country: getCountryByIso(defaultIso), local: "" };
  if (!value) return fallback;

  const raw = value.trim();
  const hasPlus = raw.startsWith("+");
  const digits = digitsOnly(raw);
  if (!digits) return fallback;

  const candidates = [...ALL_COUNTRIES].sort(
    (a, b) => b.dial.length - a.dial.length,
  );

  function matchByDialPrefix(): { country: Country; local: string } | null {
    for (const c of candidates) {
      const code = c.dial.replace("+", "");
      if (!digits.startsWith(code)) continue;
      return { country: c, local: digits.slice(code.length) };
    }
    return null;
  }

  // E.164 with "+" or full international digit strings (e.g. 5511999999999).
  if (hasPlus || digits.length > 11) {
    const matched = matchByDialPrefix();
    if (matched) return matched;
  }

  // Bare 11-digit strings are ambiguous: BR local (11999999999) or a 2+ digit
  // country code + local without "+" (e.g. Chile 56988882654 from sales_token).
  // Skip single-digit codes (+1) so BR mobiles starting with 1 are not read as US.
  if (!hasPlus && digits.length === 11) {
    for (const c of candidates) {
      const code = c.dial.replace("+", "");
      if (code.length < 2) continue;
      if (!digits.startsWith(code)) continue;
      const local = digits.slice(code.length);
      if (c.validate && !c.validate(local)) continue;
      return { country: c, local };
    }
  }

  // No dial prefix detected — assume default country, treat whole thing as local.
  return { country: getCountryByIso(defaultIso), local: digits };
}

/** Per-country validation of just the local part. */
export function isValidPhoneForCountry(country: Country, localDigits: string): boolean {
  const d = digitsOnly(localDigits);
  if (country.validate) return country.validate(d);
  return d.length >= 6 && d.length <= 14;
}

/** Max digits allowed in the local part (without country dial code). */
export function maxLocalDigits(country: Country): number {
  switch (country.iso) {
    case "BR":
      return 11;
    case "US":
    case "CA":
    case "MX":
    case "VE":
      return 10;
    case "PT":
    case "ES":
    case "CL":
    case "PE":
    case "EC":
    case "FR":
      return 9;
    case "UY":
    case "BO":
      return 8;
    case "AR":
    case "PY":
    case "DE":
    case "GB":
      return 11;
    case "IT":
      return 10;
    case "CO":
      return 10;
    default:
      return 14;
  }
}

/**
 * Formats the local phone digits for display while the user types.
 * The stored value remains digits-only; this is purely visual.
 */
export function formatPhoneLocal(country: Country, localDigits: string): string {
  const d = digitsOnly(localDigits);
  switch (country.iso) {
    case "BR": {
      if (d.length <= 2) return d.length ? `(${d}` : "";
      if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
      if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
      return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
    }
    case "US":
    case "CA": {
      if (d.length <= 3) return d;
      if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
    }
    case "PT": {
      if (d.length <= 3) return d;
      if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
      return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 9)}`;
    }
    case "ES": {
      if (d.length <= 3) return d;
      if (d.length <= 5) return `${d.slice(0, 3)} ${d.slice(3)}`;
      if (d.length <= 7) return `${d.slice(0, 3)} ${d.slice(3, 5)} ${d.slice(5)}`;
      return `${d.slice(0, 3)} ${d.slice(3, 5)} ${d.slice(5, 7)} ${d.slice(7, 9)}`;
    }
    case "MX": {
      if (d.length <= 2) return d;
      if (d.length <= 6) return `${d.slice(0, 2)} ${d.slice(2)}`;
      return `${d.slice(0, 2)} ${d.slice(2, 6)} ${d.slice(6, 10)}`;
    }
    case "AR": {
      if (d.length <= 2) return d;
      if (d.length <= 6) return `${d.slice(0, 2)} ${d.slice(2)}`;
      return `${d.slice(0, 2)} ${d.slice(2, 6)}-${d.slice(6, 10)}`;
    }
    default:
      return d.replace(/(\d{3})(?=\d)/g, "$1 ").trim();
  }
}

/**
 * Validates a fully-qualified E.164 string. Used on the backend so we don't
 * have to ship the country list there — generic 8–16 total digits is good
 * enough as a sanity check before handing off to the provider, which does
 * stricter validation server-side anyway.
 */
/**
 * The phone as Woovi gets it (server/providers/openpix.ts): digits only, with Brazil's 55 added to a
 * local 10–11 digit number.
 */
export function openPixPhoneDigits(phone: string | null | undefined): string {
  let digits = digitsOnly(phone ?? "");
  if (digits.length >= 10 && digits.length <= 11 && !digits.startsWith("55")) {
    digits = `55${digits}`;
  }
  return digits;
}

/** Woovi refuses a customer phone shorter than 12 digits: a PIX customer with one must give another. */
export function phoneUnusableForPix(phone: string | null | undefined): boolean {
  return openPixPhoneDigits(phone).length < 12;
}

export function isValidPhoneE164(value: string): boolean {
  const d = digitsOnly(value);
  return d.length >= 8 && d.length <= 16;
}
